import { api } from "@/lib/api"
import { contentFilterParams, readSavedContentFilters, type TaskContentFilter } from "@/lib/task-content-filters"

export { TASK_CONTENT_FILTERS, contentFilterParams, readSavedContentFilters, saveContentFilters, type TaskContentFilter } from "@/lib/task-content-filters"

/**
 * One definition of the tasks list query, shared by the page that renders it
 * and by anything that wants to warm it in advance.
 *
 * A prefetch under a key the page does not read is worse than no prefetch: it
 * spends the request and still leaves the page loading. Keeping the key and
 * the fetcher in one place is what stops the two drifting apart.
 */

export type TasksViewFilter = "my" | "all"
export type TasksStatusFilter = "open" | "completed" | "all"

/** Before 2026-11-03 the page wrote this on every load, chosen or not: only an admin's (who had the switch) is a real choice. */
const LEGACY_VIEW_FILTER_KEY = "tasks-view-filter"
/** Written only when someone clicks My Tasks or All Tasks. */
const VIEW_FILTER_KEY = "tasks-view-filter-choice"

/** Remember a My/All the person picked themselves. */
export function saveViewFilterChoice(view: TasksViewFilter): void {
  try {
    localStorage.setItem(VIEW_FILTER_KEY, view)
  } catch {
    // Private mode or blocked storage: the choice lasts for this visit only.
  }
}
const STATUS_FILTER_KEY = "tasks-status-filter"

// A type rather than an interface: api.tasks.list takes a Record, and only type
// aliases get the implicit index signature that makes them assignable to one.
export type TasksListParams = {
  // Omitted for General: the API defaults to it, and leaving it out keeps the
  // key identical to what the prefetch below warms.
  board?: string
  status?: TasksStatusFilter
  involving?: number
  /** A person card's "View all their tasks" (with board "all"). */
  assignee_id?: number
  tag_ids?: string
} & Partial<Record<TaskContentFilter, 1>>

/**
 * My or All, as last picked. With nothing picked it is All: the first look at
 * a board shows its tasks (on General a non-admin's All is their own tasks
 * anyway, so nothing more is revealed).
 */
export function readSavedViewFilter(isAdmin: boolean): TasksViewFilter {
  const saved = localStorage.getItem(VIEW_FILTER_KEY)
  if (saved === "my" || saved === "all") return saved
  const legacy = isAdmin ? localStorage.getItem(LEGACY_VIEW_FILTER_KEY) : null
  if (legacy === "my" || legacy === "all") return legacy
  return "all"
}

export function readSavedStatusFilter(): TasksStatusFilter {
  const saved = localStorage.getItem(STATUS_FILTER_KEY)
  if (saved === "open" || saved === "completed" || saved === "all") return saved
  return "open"
}

export function tasksListQuery(params: TasksListParams) {
  return {
    queryKey: ["tasks", params],
    queryFn: () => api.tasks.list(params),
  }
}

/**
 * What the tasks page will ask for on a cold open, derived from the same saved
 * filters its own state initialises from.
 */
export function defaultTasksListParams(userId: number | undefined, isAdmin: boolean): TasksListParams {
  const status = readSavedStatusFilter()
  const view = readSavedViewFilter(isAdmin)

  return {
    status: status === "all" ? undefined : status,
    involving: view === "my" ? userId : undefined,
    tag_ids: undefined,
    ...contentFilterParams(readSavedContentFilters()),
  }
}
